/**
 * Add field (fixes after rc.9 testing): a new field gets its label and its permanent id here. The id follows the
 * label (f_ and its slug) until it is edited, and is checked as it is typed: its shape and the draft in the browser,
 * then the workspace's history on the server (earlier model versions, stored values, watch folders, saved searches).
 * Once the field exists its id can no longer change.
 */
import { useEffect, useState } from 'react';
import { Loader2, Plus } from 'lucide-react';
import type { MetadataSchemaDefinition } from '@prism/contracts';
import { api, errorMessage } from '../../../api/client';
import { Modal } from '../../../components/Modal';
import { draftFieldIdProblem, suggestFieldId } from './modelDraftOps';

interface Availability { id: string; available: boolean; reason: string | null; message: string | null }

export function AddFieldDialog({ def, published, onCreate, onClose }: {
  def: MetadataSchemaDefinition; published: MetadataSchemaDefinition; onCreate: (label: string, id: string) => void; onClose: () => void;
}) {
  const [label, setLabel] = useState('');
  const [typedId, setTypedId] = useState<string | null>(null);
  const id = typedId ?? suggestFieldId(label);
  const local = draftFieldIdProblem(id, def, published);
  const [server, setServer] = useState<Availability | { id: string; error: string } | null>(null);

  // Ask the server once the id is well formed and the typing has paused.
  useEffect(() => {
    if (local) return;
    let live = true;
    const t = window.setTimeout(() => {
      api<Availability>(`/api/v1/metadata/field-ids/${encodeURIComponent(id)}/available`)
        .then((r) => { if (live) setServer(r); })
        .catch((err) => { if (live) setServer({ id, error: errorMessage(err) }); });
    }, 250);
    return () => { live = false; window.clearTimeout(t); };
  }, [id, local]);

  const checked = server && server.id === id ? server : null;
  // Nothing to say before anything is typed; after that, the browser's own check first, then the server's.
  const problem = !id && typedId === null && !label.trim() ? null : local ??(checked && 'error' in checked ? checked.error : checked && !checked.available ? checked.message : null);
  const checking = !local && !checked;
  const ready = Boolean(label.trim() && id && !local && checked && !('error' in checked) && checked.available);
  const create = () => { if (ready) onCreate(label.trim(), id); };

  return (
    <Modal title="Add field" onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>Cancel</button>
      <button className="btn primary" disabled={!ready} onClick={create} data-testid="create-field"><Plus size={14} /> Add field</button>
    </>}>
      <form style={{ display: 'flex', flexDirection: 'column', gap: 14 }} onSubmit={(e) => { e.preventDefault(); create(); }} data-testid="add-field-dialog">
        <div className="field">
          <label htmlFor="new-field-label">Label</label>
          <input id="new-field-label" className="input" value={label} autoFocus maxLength={200} data-testid="new-field-label"
            onChange={(e) => setLabel(e.target.value)} />
          <span className="help">What people see on the form. It can be changed later.</span>
        </div>
        <div className="field">
          <label htmlFor="new-field-id">Field id</label>
          <input id="new-field-id" className={`input mono ${problem ? 'invalid' : ''}`} value={id} spellCheck={false} autoComplete="off" maxLength={60}
            aria-invalid={Boolean(problem)} aria-describedby="new-field-id-help" data-testid="new-field-id"
            onChange={(e) => setTypedId(e.target.value)} />
          {problem
            ? <span className="error-text" role="alert" data-testid="new-field-id-error">{problem}</span>
            : <span className="help" id="new-field-id-help" data-testid="new-field-id-help">
                {checking && id ? <><Loader2 size={11} className="spin" /> Checking…</> : 'Permanent: stored values, sidecar mappings, searches and workflows use it. It cannot be changed once the field exists.'}
              </span>}
          {typedId !== null && typedId !== suggestFieldId(label) && label.trim() && (
            <button type="button" className="link-button" style={{ alignSelf: 'flex-start', fontSize: 12 }} onClick={() => setTypedId(null)} data-testid="new-field-id-reset">
              Use {suggestFieldId(label) || 'the label'} again
            </button>
          )}
        </div>
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
